import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Trash2, X, Loader2, Edit, Eye } from 'lucide-react';
import { getItemsServices, createQuoteItem, updateQuoteItem, deleteQuoteItem, getWorkersDropdown } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { canSeePrices } from '../../utils/permissions';
import { SearchableSelect } from '../SearchableSelect';

export function CostBuilderModal({ isOpen, onClose, descriptionId, quoteId, existingItems = [], onAddItem, onRemoveItem, onUpdateItem, refetch, readOnly = false }) {
  const { user: currentUser } = useAuth();
  const showPrices = canSeePrices(currentUser);
  const [activeTab, setActiveTab] = useState('materials');
  const [editingCell, setEditingCell] = useState(null); // { itemId, field }
  const [editValue, setEditValue] = useState('');
  const [miscCustomName, setMiscCustomName] = useState('');
  const [formData, setFormData] = useState({
    item_type: 1, item_name: '', uom: 'Each', qty: 1, cost: 0, markup: 0, sell_price: 0, item_id: ''
  });

  const { data: materials } = useQuery({
    queryKey: ['itemsServices'],
    queryFn: () => getItemsServices().then(res => res.data),
    enabled: isOpen
  });

  const tabs = [
    { id: 'materials', label: 'Materials', type: 1 },
    { id: 'misc', label: 'MISC', type: 2 },
    { id: 'labour', label: 'Labour', type: 3 },
    { id: 'other', label: 'Other', type: 4 },
  ];

  // Single source of truth for the item_type — derived from the visible tab.
  const typeMap = { materials: 1, misc: 2, labour: 3, other: 4 };
  const currentType = typeMap[activeTab] || 1;

  const resetForm = () => {
    // Keep the current tab's type — don't silently revert to Materials.
    setFormData({ item_type: currentType, item_name: '', uom: 'Each', qty: 1, cost: 0, markup: 0, sell_price: 0, item_id: '' });
    setMiscCustomName('');
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab.id);
    setFormData({ item_type: tab.type, item_name: '', uom: 'Each', qty: 1, cost: 0, markup: 0, sell_price: 0, item_id: '' });
    setMiscCustomName('');
  };

  const handleItemSelect = (itemId) => {
    if (!itemId) {
      setFormData(prev => ({ ...prev, item_id: '', item_name: '' }));
      return;
    }
    const item = materials?.find(m => m.id === itemId);
    if (item) {
      setFormData(prev => ({
        ...prev,
        item_type: currentType,     // lock to active tab, never borrow catalog item's type
        item_id: item.id,
        item_name: item.name,
        cost: item.cost || 0,
        sell_price: item.sell_price || 0,
        uom: item.uom || 'Each'
      }));
      setMiscCustomName('');
    }
  };

  const handleMarkupChange = (markupPercent) => {
    const newSellPrice = formData.cost * (1 + markupPercent / 100);
    setFormData({ ...formData, markup: markupPercent, sell_price: Math.round(newSellPrice * 100) / 100 });
  };

  const totalCost = formData.qty * (formData.sell_price || formData.cost);

  const handleAdd = () => {
    const itemName = activeTab === 'misc' && miscCustomName ? miscCustomName : formData.item_name;
    if (!itemName) {
      toast.error('Please enter/select item');
      return;
    }
    // Source of truth for the type is the visible tab, not formData
    // (guards against any stale state after tab changes or async updates).
    const itemType = currentType;
    const priceEach = formData.sell_price || formData.cost;
    const totalEach = (formData.qty || 0) * priceEach;

    // Every "Add" click creates a new line item — no auto-merging.
    // If the user wants to combine duplicates, they can bump the qty manually.
    onAddItem({
      quote_id: quoteId,
      quote_description_id: descriptionId,
      item_type: itemType,
      item_name: itemName,
      uom: formData.uom,
      qty: formData.qty,
      cost: formData.cost,
      markup: formData.markup || 0,
      sell_price: formData.sell_price,
      total_cost: totalEach
    });
    resetForm();
  };

  const handleInlineEdit = (itemId, field, currentValue) => {
    setEditingCell({ itemId, field });
    setEditValue(String(currentValue));
  };

  const handleInlineSave = (item) => {
    const val = parseFloat(editValue) || 0;
    const newQty = editingCell.field === 'qty' ? val : (item.qty || 0);
    const newCost = editingCell.field === 'cost' ? val : (item.cost || 0);
    const curCost = item.cost || 0;
    const curSell = (item.sell_price ?? item.cost) || 0;
    // Editing qty keeps the existing sell price. Editing cost preserves the
    // item's existing margin ratio (derived from its current cost/sell), so the
    // markup isn't lost — this matters most for MISC where sell != cost. Derived
    // from live values so it works even when a stored markup field is missing.
    let newSellPrice = curSell;
    if (editingCell.field === 'cost') {
      const ratio = curCost > 0 ? curSell / curCost : 1;
      newSellPrice = Math.round(newCost * ratio * 100) / 100;
    }
    onUpdateItem(item.id, {
      qty: newQty,
      cost: newCost,
      sell_price: newSellPrice,
      total_cost: Math.round(newQty * newSellPrice * 100) / 100
    });
    setEditingCell(null);
  };

  const itemsByType = {
    materials: existingItems?.filter(i => i.item_type === 1) || [],
    misc: existingItems?.filter(i => i.item_type === 2) || [],
    labour: existingItems?.filter(i => i.item_type === 3) || [],
    other: existingItems?.filter(i => i.item_type === 4) || [],
  };

  const totals = {
    materials: itemsByType.materials.reduce((s, i) => s + (i.total_cost || 0), 0),
    misc: itemsByType.misc.reduce((s, i) => s + (i.total_cost || 0), 0),
    labour: itemsByType.labour.reduce((s, i) => s + (i.total_cost || 0), 0),
    other: itemsByType.other.reduce((s, i) => s + (i.total_cost || 0), 0),
  };
  const grandTotal = totals.materials + totals.misc + totals.labour + totals.other;
  const currentItems = itemsByType[activeTab] || [];

  if (!isOpen) return null;

  const getDropdownItems = () => {
    return materials?.filter(m => m.item_type === currentType) || [];
  };

  const getDropdownLabel = () => {
    const map = { materials: 'Select Item', misc: 'Select Item', labour: 'Select Labour', other: 'Select Item' };
    return map[activeTab] || 'Select Item';
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="cost-builder-modal">
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="font-semibold text-lg">{readOnly ? 'Quote Costing' : 'Add Item(s)'}</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Tabs + Form + Table in a bordered section */}
          <div className="border rounded">
            {/* Tabs */}
            <div className="flex border-b">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab)}
                  className={`px-5 py-2.5 text-sm font-medium transition-colors ${
                    activeTab === tab.id 
                      ? 'bg-white border-b-2 border-primary text-slate-800' 
                      : 'text-slate-500 hover:text-slate-700 bg-slate-50'
                  }`}
                  data-testid={`cost-tab-${tab.id}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Add form - shown at the TOP */}
            {!readOnly && (
              <div className="p-4 space-y-3 border-b bg-white">
                {/* Item selection */}
                <div>
                  <label className="block text-xs font-semibold mb-1">Item</label>
                  {activeTab === 'misc' ? (
                    <div className="space-y-2">
                      <div className="max-w-md">
                        <SearchableSelect
                          value={formData.item_id}
                          onChange={handleItemSelect}
                          placeholder={getDropdownLabel()}
                          testId="misc-item-dropdown"
                          options={getDropdownItems().map(m => ({
                            value: m.id,
                            label: `${m.item_number ? `${m.item_number} - ` : ''}${m.name}`,
                          }))}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500">or type custom:</span>
                        <input
                          type="text"
                          value={miscCustomName}
                          onChange={(e) => { setMiscCustomName(e.target.value); setFormData(prev => ({ ...prev, item_name: e.target.value, item_id: '' })); }}
                          placeholder="Enter custom MISC item name"
                          className="flex-1 max-w-md px-3 py-2 border rounded text-sm"
                          data-testid="misc-custom-input"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="max-w-md">
                      <SearchableSelect
                        value={formData.item_id}
                        onChange={handleItemSelect}
                        placeholder={getDropdownLabel()}
                        testId="item-dropdown"
                        options={getDropdownItems().map(m => ({
                          value: m.id,
                          label: activeTab === 'labour'
                            ? (showPrices ? `${m.name} - $${m.cost}/hr` : m.name)
                            : `${m.item_number ? `${m.item_number} - ` : ''}${m.name}`,
                        }))}
                      />
                    </div>
                  )}
                </div>

                {/* Fields row */}
                <div className="flex items-end gap-3 flex-wrap">
                  <div className="w-20">
                    <label className="block text-xs font-semibold mb-1">UOM</label>
                    <input type="text" value={formData.uom} onChange={(e) => setFormData({ ...formData, uom: e.target.value })} className="w-full px-2 py-2 border rounded text-sm" data-testid="item-uom" />
                  </div>
                  <div className="w-20">
                    <label className="block text-xs font-semibold mb-1">QTY</label>
                    <input type="number" value={formData.qty} onChange={(e) => setFormData({ ...formData, qty: parseFloat(e.target.value) || 0 })} className="w-full px-2 py-2 border rounded text-sm text-center" step="0.5" data-testid="item-qty" />
                  </div>
                  {showPrices && (
                    <>
                      <div className="w-24">
                        <label className="block text-xs font-semibold mb-1">Cost</label>
                        <input type="number" value={formData.cost} onChange={(e) => { const c = parseFloat(e.target.value) || 0; const sp = activeTab === 'misc' && formData.markup ? c * (1 + formData.markup / 100) : formData.sell_price; setFormData({ ...formData, cost: c, sell_price: Math.round(sp * 100) / 100 }); }} className="w-full px-2 py-2 border rounded text-sm text-right" step="0.01" data-testid="item-cost" />
                      </div>
                      {activeTab === 'misc' && (
                        <div className="w-24">
                          <label className="block text-xs font-semibold mb-1">Mark up(%)</label>
                          <input type="number" value={formData.markup} onChange={(e) => handleMarkupChange(parseFloat(e.target.value) || 0)} className="w-full px-2 py-2 border rounded text-sm text-right" step="1" data-testid="item-markup" />
                        </div>
                      )}
                      <div className="w-24">
                        <label className="block text-xs font-semibold mb-1">Sell Price</label>
                        <input type="number" value={formData.sell_price} onChange={(e) => setFormData({ ...formData, sell_price: parseFloat(e.target.value) || 0 })} className="w-full px-2 py-2 border rounded text-sm text-right" step="0.01" data-testid="item-sell-price" />
                      </div>
                      <div className="w-24">
                        <label className="block text-xs font-semibold mb-1">Total Cost</label>
                        <div className="px-2 py-2 bg-slate-100 border rounded text-sm text-right font-medium" data-testid="item-total">{totalCost.toFixed(2)}</div>
                      </div>
                    </>
                  )}
                  <button onClick={handleAdd} className="px-5 py-2 bg-cyan-500 text-white rounded text-sm hover:bg-cyan-600 font-medium" data-testid="add-item-btn">Add</button>
                </div>
              </div>
            )}

            {/* Items table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="cost-items-table">
                <thead>
                  <tr className="bg-slate-50 border-b">
                    <th className="p-2.5 text-left font-semibold">Item</th>
                    <th className="p-2.5 text-center font-semibold w-16">UOM</th>
                    <th className="p-2.5 text-center font-semibold w-16">Qty</th>
                    {showPrices && <th className="p-2.5 text-right font-semibold w-24">Cost</th>}
                    {showPrices && activeTab === 'misc' && <th className="p-2.5 text-right font-semibold w-24">Mark up%</th>}
                    {showPrices && activeTab === 'misc' && <th className="p-2.5 text-right font-semibold w-24">Sell Price</th>}
                    {showPrices && <th className="p-2.5 text-right font-semibold w-24">Total</th>}
                    {!readOnly && <th className="p-2.5 w-10"></th>}
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length === 0 ? (
                    <tr><td colSpan={showPrices ? (activeTab === 'misc' ? 8 : 6) : (readOnly ? 3 : 4)} className="p-4 text-center text-slate-400">No items in this category</td></tr>
                  ) : (
                    currentItems.map((item) => {
                      const itemCost = item.cost || 0;
                      const itemSellPrice = item.sell_price || item.cost || 0;
                      const itemMarkup = itemCost > 0 ? ((itemSellPrice - itemCost) / itemCost * 100) : 0;
                      return (
                      <tr key={item.id} className="border-b hover:bg-slate-50">
                        <td className="p-2.5">{item.item_name}</td>
                        <td className="p-2.5 text-center">{item.uom}</td>
                        <td className="p-2.5 text-center">
                          {!readOnly && editingCell?.itemId === item.id && editingCell?.field === 'qty' ? (
                            <input type="number" value={editValue} onChange={e => setEditValue(e.target.value)}
                              onBlur={() => handleInlineSave(item)} onKeyDown={e => e.key === 'Enter' && handleInlineSave(item)}
                              className="w-16 px-1 py-0.5 border rounded text-center text-sm" autoFocus step="0.5" min="0" />
                          ) : (
                            <span className={!readOnly ? 'cursor-pointer hover:bg-slate-100 px-1 rounded' : ''} 
                              onClick={() => !readOnly && handleInlineEdit(item.id, 'qty', item.qty)}>{item.qty}</span>
                          )}
                        </td>
                        {showPrices && (
                          <td className="p-2.5 text-right">
                            {!readOnly && editingCell?.itemId === item.id && editingCell?.field === 'cost' ? (
                              <input type="number" value={editValue} onChange={e => setEditValue(e.target.value)}
                                onBlur={() => handleInlineSave(item)} onKeyDown={e => e.key === 'Enter' && handleInlineSave(item)}
                                className="w-20 px-1 py-0.5 border rounded text-right text-sm" autoFocus step="0.01" min="0" />
                            ) : (
                              <span className={!readOnly ? 'cursor-pointer hover:bg-slate-100 px-1 rounded' : ''}
                                onClick={() => !readOnly && handleInlineEdit(item.id, 'cost', itemCost)}>{itemCost.toFixed(2)}</span>
                            )}
                          </td>
                        )}
                        {showPrices && activeTab === 'misc' && <td className="p-2.5 text-right">{itemMarkup.toFixed(1)}%</td>}
                        {showPrices && activeTab === 'misc' && <td className="p-2.5 text-right">{itemSellPrice.toFixed(2)}</td>}
                        {showPrices && <td className="p-2.5 text-right font-medium">{item.total_cost?.toFixed(2)}</td>}
                        {!readOnly && (
                          <td className="p-2.5 text-center">
                            <button onClick={() => onRemoveItem(item.id)} className="text-red-500 hover:text-red-700" data-testid={`remove-item-${item.id}`}>
                              <Trash2 size={14} />
                            </button>
                          </td>
                        )}
                      </tr>
                      );
                    })
                  )}
                  {/* TOTAL row for current tab - hidden for Ops */}
                  {showPrices && currentItems.length > 0 && (
                    <tr className="bg-slate-50 font-bold">
                      <td colSpan={activeTab === 'misc' ? 6 : 4} className="p-2.5">TOTAL</td>
                      <td className="p-2.5 text-right">{(totals[activeTab] || 0).toFixed(2)}</td>
                      {!readOnly && <td></td>}
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Total Cost Summary - matching live system style - hidden from Ops */}
          {showPrices && (
            <div className="border rounded overflow-hidden" data-testid="total-cost-summary">
              <div className="bg-cyan-500 text-white px-4 py-2.5 font-bold text-sm">Total Cost</div>
              <table className="w-full text-sm">
                <tbody>
                  <tr className="border-b">
                    <td className="px-4 py-2">Materials</td>
                    <td className="px-4 py-2 text-right font-medium w-32">{totals.materials.toFixed(2)}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="px-4 py-2">Miscellaneous</td>
                    <td className="px-4 py-2 text-right font-medium w-32">{totals.misc.toFixed(2)}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="px-4 py-2">Labour</td>
                    <td className="px-4 py-2 text-right font-medium w-32">{totals.labour.toFixed(2)}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="px-4 py-2">Other</td>
                    <td className="px-4 py-2 text-right font-medium w-32">{totals.other.toFixed(2)}</td>
                  </tr>
                  <tr className="bg-green-500 text-white font-bold text-base">
                    <td className="px-4 py-3">Total</td>
                    <td className="px-4 py-3 text-right w-32">{grandTotal.toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="p-4 border-t flex justify-end">
          <button onClick={onClose} className="px-5 py-2 text-sm border border-slate-300 rounded bg-slate-100 hover:bg-slate-200" data-testid="close-cost-builder">Close</button>
        </div>
      </div>
    </div>
  );
}

// Add/Edit Description Modal - Updated with Job Title Typeahead
